// next
import { useRouter } from 'next/router';
// hooks
import useResponsive from '../../hooks/useResponsive';
// @mui
import {
  Box,
  Button,
  Typography,
  Stack,
  Chip,
  AvatarGroup,
  Divider,
} from '@mui/material';
import { avatarGroupClasses } from '@mui/material/AvatarGroup';
import { alpha } from '@mui/material/styles';
// components
import Iconify from '../../components/Iconify';
import Image from '../../components/Image';
import Label from '../../components/Label';
import Avatar from '../../components/Avatar';
import SvgIconStyle from '../../components/SvgIconStyle';
import DIOText from '../../components/DIOText';
import Markdown from '../../components/Markdown';

// ----------------------------------------------------------------------

export default function ProfileDetail({ sx, profile, goEdit }) {
  const smUp = useResponsive('up', 'sm');

  return (
    <Stack sx={{ width: 1, ...sx }} spacing={3}>

      {/* header */}
      <Stack direction="row" spacing={10}>
        <Stack direction="row" sx={{ width: 1 }} spacing={smUp ? 3 : 2}>
          <Box
            sx={{
              borderRadius: 2,
              minWidth: smUp ? 160 : 72,
              width: smUp ? 160 : 72, height: smUp ? 160 : 72
            }}
          >
            <Image
              src={profile?.logo_file_path || 'https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/images/fliers-no-logo-2x.png'}
              alt="company-logo"
              sx={{ width: smUp ? 160 : 72, height: smUp ? 160 : 72, minWidth: smUp ? 160 : 72, minHeight: smUp ? 160 : 72, borderRadius: 2, border: (theme) => `1px solid ${theme.palette.grey[500_24]}` }}
            />
          </Box>
          <Stack spacing={1}>
            <Typography variant={smUp ? "subtitle1" : "subtitle2"} color="info.main" sx={{ wordBreak: "break-all" }}>
              {profile?.name}
            </Typography>
            <Typography variant="h4" sx={{ wordBreak: "break-all" }}>
              {profile?.appeal_title}
            </Typography>
            <Stack direction="row" alignItems="center" sx={{ flexWrap: "wrap", gap: 1 }}>
              {profile?.keywords?.split(' ').map((keyword, index) => (
                <Chip label={keyword} key={index} color="info" size={smUp ? "medium" : "small"} variant="outlined" sx={{ borderColor: "info.main" }} />
              ))}
            </Stack>
            {!smUp && (
              <Box>
                <Button
                  variant="outlined"
                  sx={{
                    maxWidth: 180,
                    borderRadius: 50,
                  }}
                  endIcon={<Iconify icon="ic:chevron-right" />}
                  onClick={goEdit}
                >
                  회사 정보 수정하기
                </Button>
              </Box>
            )}
          </Stack>
        </Stack>
        {smUp && (
          <Box>
            <Button
              variant="outlined"
              sx={{
                maxWidth: 180,
                minWidth: 160,
                mt: 2,
                borderRadius: 50,
              }}
              endIcon={<Iconify icon="ic:chevron-right" />}
              onClick={goEdit}
            >
              회사 정보 수정하기
            </Button>
          </Box>
        )}
      </Stack>

      <Divider />

      {/* info */}
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SvgIconStyle src={'/icons/icon-building-mono.svg'} sx={{ width: 24, height: 24, color: "secondary.main" }} />
          <Typography variant="h6">
            회사 소개
          </Typography>
        </Stack>
        <Stack sx={{ pl: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
          <Markdown>
            {profile?.description}
          </Markdown>
        </Stack>
      </Stack>

      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SvgIconStyle src={'/icons/icon-heart-mono.svg'} sx={{ width: 24, height: 24, color: "secondary.main" }} />
          <Typography variant="h6">
            만나보고 싶은 포지션
          </Typography>
        </Stack>
        <Stack sx={{ pl: 4, flexWrap: 'wrap', gap: 1 }} direction="row">
          {profile?.coffee_chat_positions?.map((c, index) => (
            <Chip key={index} label={`${c.role}${c.position === 'developer' ? ' 개발자' : ''}`} />
          ))}
        </Stack>
      </Stack>

      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SvgIconStyle src={'/icons/icon-user-two-mono.svg'} sx={{ width: 24, height: 24, color: "secondary.main" }} />
          <Typography variant="h6">
            만나보고 싶은 이유
          </Typography>
        </Stack>
        <Stack sx={{ pl: 4, flexWrap: 'wrap', gap: 1 }}>
          {profile?.need_casual_chat && (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap alignItems={!smUp ? "flex-start" : "center"}>
            <Chip label="캐주얼 커피챗" />
            <Typography variant="body2" color="text.secondary">
              회사와 관련한 가벼운 이야기 나눠보고 싶어요.
            </Typography>
          </Stack>
          )}
          {profile?.need_part_time && (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap alignItems={!smUp ? "flex-start" : "center"}>
            <Chip label="파트타임 진행" />
            <Typography variant="body2" color="text.secondary">
              파트타임으로 업무를 진행해보고 싶어요.
            </Typography>
          </Stack>
          )}
          {profile?.need_mini_project && (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap alignItems={!smUp ? "flex-start" : "center"}>
            <Chip label="미니프로젝트 진행" />
            <Typography variant="body2" color="text.secondary">
              채용 제안 전, 2주동안 미니 프로젝트를 진행해보고 싶어요.
            </Typography>
          </Stack>
          )}
        </Stack>
      </Stack>

      <Divider sx={{ pt: 1 }} />

      {profile?.dio_recommendation && (
      <Stack spacing={1} sx={{ pt: 1 }}>
        <Typography variant="h6">
          <DIOText />팀의 추천 메시지
        </Typography>
        <Typography variant="body2">
          제출한 회사 소개를 기반으로 <DIOText />팀에서 직접 작성한 메시지에요. 추천 메시지는 크루에게도 같은 내용이 보여요.
        </Typography>
        <Box
          sx={{ p: 3, bgcolor: "grey.200", borderRadius: 2 }}
        >
          <Typography>
            {profile?.dio_recommendation}
          </Typography>
        </Box>
      </Stack>
      )}

      <Stack spacing={1} sx={{ pt: 1 }}>
        <Typography variant="h6">
          우리 회사와 커피챗 후기
        </Typography>
        {profile?.coffee_chat_reviews?.length === 0 ? (
          <Typography variant="body2">
           아직 작성된 커피챗 후기가 없어요.
          </Typography>
        ) : (
          <Typography variant="body2">
            실제 커피챗을 진행한 크루가 작성한 내용으로, <DIOText />팀에서 검수하여 공개 가능한 후기만 보여드려요.
          </Typography>
        )}
        {profile?.coffee_chat_reviews?.map((review, index) => (
          <Box
            key={index}
            sx={{ p: 3, bgcolor: (theme) => alpha(theme.palette.info.main, 0.08), borderRadius: 2 }}
          >
            <Stack sx={{ mb: 2 }} direction="row" alignItems="center" spacing={1}>
              <Avatar alt={`크루-${index}`} src={`https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/crewji/crewji_${review.crew_hash}.png`} />
              <Stack>
                <Typography variant="overline" sx={{ color: "info.main" }}>
                  {profile?.name} 팀과 커피챗
                </Typography>
                <Typography variant="subtitle1">
                  {review.crew_nickname}
                  <Typography variant="subtitle1" component="span" sx={{ color: "text.secondary" }}>{" "}크루</Typography>
                </Typography>
              </Stack>
            </Stack>
            <Typography>
              {review?.message}
            </Typography>
          </Box>
        ))}
      </Stack>

    </Stack>
  )
}
